import React, { useState, useCallback } from 'react';
import { Upload, X, GripVertical, RotateCw } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUpload = ({ photos = [], onChange, maxPhotos = 5 }) => {
  const [uploading, setUploading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Client-side image compression before upload
  const compressImage = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Resize if larger than 1920px
          const maxDimension = 1920;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob with quality 0.85
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // Create a new File object with the original name
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(compressedFile);
              } else {
                reject(new Error('Canvas to Blob conversion failed'));
              }
            },
            'image/jpeg',
            0.85
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = useCallback(async (files) => {
    if (!files || files.length === 0) {
      return;
    }
    
    // Special handling for single photo mode (maxPhotos === 1)
    if (maxPhotos === 1) {
      // In single photo mode, always allow replacement
      const filesToUpload = Array.from(files).slice(0, 1);
      
      setUploading(true);
      
      try {
        const uploadedUrls = [];
        
        for (const file of filesToUpload) {
          // Validate file size (21MB)
          if (file.size > 21 * 1024 * 1024) {
            alert(`${file.name} is too large. Maximum size is 21MB.`);
            continue;
          }

          // Validate file type
          if (!file.type.startsWith('image/')) {
            alert(`${file.name} is not an image file.`);
            continue;
          }

          const formData = new FormData();
          formData.append('file', file);

          // Try authenticated upload first (cookie will be sent automatically if present)
          try {
            const response = await axios.post(
              `${API_URL}/api/upload/upload-photo`,
              formData,
              { 
                withCredentials: true,
                headers: {
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
            
            if (response.data.success) {
              uploadedUrls.push(response.data.url);
            }
          } catch (authError) {
            // If authenticated upload fails (e.g., during profile creation), use temp upload
            const response = await axios.post(
              `${API_URL}/api/upload/upload-temp-photo`,
              formData,
              { 
                headers: {
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
            
            if (response.data.success) {
              uploadedUrls.push(response.data.url);
            }
          }
        }

        if (uploadedUrls.length > 0) {
          // Replace the existing photo instead of adding
          onChange(uploadedUrls);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Upload failed:', error);
        }
        
        // Show more specific error message if available
        let errorMessage = 'Failed to upload photo. Please try again.';
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          errorMessage = 'Upload timed out. Please check your internet connection and try again.';
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.message?.includes('Network Error')) {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        } else if (error.message) {
          errorMessage = `Upload error: ${error.message}`;
        }
        
        alert(errorMessage);
      } finally {
        setUploading(false);
      }
      return;
    }
    
    // Multiple photo mode - check remaining slots
    const remainingSlots = maxPhotos - photos.length;
    if (remainingSlots <= 0) {
      alert(`Maximum ${maxPhotos} photos allowed`);
      return;
    }

    const filesToUpload = Array.from(files).slice(0, remainingSlots);
    
    setUploading(true);
    
    try {
      const uploadedUrls = [];
      
      for (const file of filesToUpload) {
        // Validate file size (21MB)
        if (file.size > 21 * 1024 * 1024) {
          alert(`${file.name} is too large. Maximum size is 21MB.`);
          continue;
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
          alert(`${file.name} is not an image file.`);
          continue;
        }

        // Compress image on client side before upload (resize + quality reduction)
        let fileToUpload = file;
        try {
          fileToUpload = await compressImage(file);
          if (process.env.NODE_ENV === 'development') {
            console.log(`Compressed ${file.name}: ${(file.size / 1024).toFixed(0)}KB → ${(fileToUpload.size / 1024).toFixed(0)}KB`);
          }
        } catch (compressionError) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Client-side compression failed, uploading original:', compressionError);
          }
          // Fall back to original file if compression fails
          fileToUpload = file;
        }

        const formData = new FormData();
        formData.append('file', fileToUpload);

        // Try authenticated upload first (cookie will be sent automatically if present)
        try {
          const response = await axios.post(
            `${API_URL}/api/upload/upload-photo`,
            formData,
            { 
              withCredentials: true,
              headers: {
                'Content-Type': 'multipart/form-data'
              },
              timeout: 120000 // 2 minute timeout (increased for large uploads)
            }
          );
          
          if (response.data.success) {
            uploadedUrls.push(response.data.url);
          }
        } catch (authError) {
          // If authenticated upload fails (e.g., during profile creation), use temp upload
          const response = await axios.post(
            `${API_URL}/api/upload/upload-temp-photo`,
            formData,
            { 
              headers: {
                'Content-Type': 'multipart/form-data'
              },
              timeout: 120000 // 2 minute timeout (increased for large uploads)
            }
          );
          
          if (response.data.success) {
            uploadedUrls.push(response.data.url);
          }
        }
      }

      if (uploadedUrls.length > 0) {
        // Add new photos to existing array
        onChange([...photos, ...uploadedUrls]);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Upload failed:', error);
      }
      
      // Show more specific error message if available
      let errorMessage = 'Failed to upload photo. Please try again.';
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Upload timed out. Please check your internet connection and try again.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message?.includes('Network Error')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error.message) {
        errorMessage = `Upload error: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setUploading(false);
    }
  }, [photos, maxPhotos, onChange]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDelete = async (photoUrl, index) => {
    try {
      // Try authenticated delete (cookie will be sent automatically if present)
      try {
        await axios.delete(`${API_URL}/api/upload/photo?photo_url=${encodeURIComponent(photoUrl)}`, {
          withCredentials: true
        });
      } catch (authError) {
        // If authenticated delete fails, try temp delete (for non-authenticated users)
        await axios.delete(`${API_URL}/api/upload/temp-photo?photo_url=${encodeURIComponent(photoUrl)}`);
      }

      const newPhotos = photos.filter((_, i) => i !== index);
      onChange(newPhotos);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Delete failed:', error);
      }
      alert('Failed to delete photo');
    }
  };

  const handleRotate = async (photoUrl) => {
    try {
      // Try authenticated rotate first
      try {
        const response = await axios.post(
          `${API_URL}/api/upload/rotate-photo`,
          { photo_url: photoUrl, rotation: 90 },
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        
        // Update photo URL with new timestamp if provided
        if (response.data.success && response.data.url) {
          const newPhotos = photos.map(url => 
            url.split('?')[0] === photoUrl.split('?')[0] ? response.data.url : url
          );
          onChange(newPhotos);
        } else {
          // Fallback: Force re-render by adding timestamp
          const newPhotos = photos.map(url => 
            url.split('?')[0] === photoUrl.split('?')[0] 
              ? `${url.split('?')[0]}?t=${Date.now()}` 
              : url
          );
          onChange(newPhotos);
        }
      } catch (authError) {
        // Temp rotate (no auth)
        const response = await axios.post(
          `${API_URL}/api/upload/rotate-temp-photo`,
          { photo_url: photoUrl, rotation: 90 },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        
        // Update photo URL with new timestamp
        if (response.data.success && response.data.url) {
          const newPhotos = photos.map(url => 
            url.split('?')[0] === photoUrl.split('?')[0] ? response.data.url : url
          );
          onChange(newPhotos);
        }
      }
    } catch (error) {
      alert('Failed to rotate photo');
    }
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragEnter = (index) => {
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newPhotos = [...photos];
    const [removed] = newPhotos.splice(draggedIndex, 1);
    newPhotos.splice(index, 0, removed);
    
    onChange(newPhotos);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    setDraggedIndex(null);
    
    // Save new order to backend
    try {
      await axios.put(
        `${API_URL}/api/upload/reorder-photos`,
        { photo_urls: photos },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save photo order:', error);
      }
    }
  };

  const getFullUrl = (url) => {
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  return (
    <div className="space-y-4">
      {/* Single Photo Display */}
      <div className="flex justify-center">
        {photos.length > 0 ? (
          <div className="relative w-64 h-64 rounded-lg overflow-hidden bg-gray-100 group">
            <img
              src={getFullUrl(photos[0])}
              alt="Profile"
              className="w-full h-full object-cover"
            />
            
            {/* Clickable overlay to change photo */}
            <label className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-40 transition-all cursor-pointer flex items-center justify-center">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  // Upload new photo first, which will replace the old one
                  await handleFileSelect(e.target.files);
                  // The new photo will be added to the array, so we need to remove the old one
                  if (photos.length > 1) {
                    // Delete the old photo (now at index 0) after new one is uploaded
                    await handleDelete(photos[0], 0);
                  }
                }}
                disabled={uploading}
              />
              <Upload className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </label>
            
            {/* Rotate button */}
            <button
              type="button"
              onClick={() => handleRotate(photos[0])}
              className="absolute top-4 left-1/2 -translate-x-1/2 bg-blue-500 hover:bg-blue-600 text-white rounded-full p-2 transition-colors shadow-lg z-10"
              title="Rotate 90° clockwise"
            >
              <RotateCw className="w-5 h-5" />
            </button>

            {/* Delete button */}
            <button
              type="button"
              onClick={() => handleDelete(photos[0], 0)}
              className="absolute top-4 right-4 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 transition-colors shadow-lg z-10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <label
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="w-64 h-64 rounded-lg border-2 border-dashed border-gray-300 hover:border-purple-500 flex flex-col items-center justify-center cursor-pointer transition-colors bg-gray-50 hover:bg-gray-100"
          >
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files)}
              disabled={uploading}
            />
            {uploading ? (
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-sm text-gray-500">Uploading...</p>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 text-gray-400 mb-4" />
                <p className="text-base text-gray-600 font-semibold mb-2">
                  Upload your profile photo
                </p>
                <p className="text-sm text-gray-500 text-center px-4">
                  Click or drag to upload
                </p>
              </>
            )}
          </label>
        )}
      </div>

      {/* Info text */}
      {photos.length > 0 && (
        <p className="text-sm text-gray-500 text-center">
          Click photo to change • Click <RotateCw className="w-3 h-3 inline" /> to rotate • Click <X className="w-3 h-3 inline" /> to remove
        </p>
      )}
    </div>
  );
};

export default ImageUpload;
